import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import { AISidebar, AIRecommendationPanel } from '../components/ai';
import { Users, Heart, Leaf, TrendingUp, AlertCircle, Gem } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
// Mock data for segments
const segmentData = [{
  name: 'Studenti',
  value: 145,
  color: '#1a4d2e'
}, {
  name: 'Professionisti',
  value: 98,
  color: '#2d7a4f'
}, {
  name: 'Famiglie',
  value: 67,
  color: '#4aa770'
}, {
  name: 'Senior',
  value: 42,
  color: '#7bc99c'
}];
const segmentTrendData = [{
  month: 'Gen',
  studenti: 120,
  professionisti: 85,
  famiglie: 55,
  senior: 35
}, {
  month: 'Feb',
  studenti: 128,
  professionisti: 90,
  famiglie: 58,
  senior: 38
}, {
  month: 'Mar',
  studenti: 135,
  professionisti: 92,
  famiglie: 62,
  senior: 40
}, {
  month: 'Apr',
  studenti: 145,
  professionisti: 98,
  famiglie: 67,
  senior: 42
}];
const preferencesData = [{
  name: 'Prodotti eco-friendly',
  value: 234
}, {
  name: 'Esperienze sostenibili',
  value: 198
}, {
  name: 'Mobilità verde',
  value: 167
}, {
  name: 'Alimentazione bio',
  value: 145
}, {
  name: 'Energia rinnovabile',
  value: 123
}];
const habitsData = [{
  name: 'Riciclo quotidiano',
  frequency: 'Sempre',
  value: 89
}, {
  name: 'Trasporto pubblico',
  frequency: 'Spesso',
  value: 72
}, {
  name: 'Riduzione plastica',
  frequency: 'Sempre',
  value: 85
}, {
  name: 'Acquisti locali',
  frequency: 'Spesso',
  value: 68
}, {
  name: 'Risparmio energetico',
  frequency: 'Sempre',
  value: 91
}];
const actionsData = [{
  category: 'Mobilità',
  count: 456
}, {
  category: 'Riciclo',
  count: 389
}, {
  category: 'Energia',
  count: 312
}, {
  category: 'Alimentazione',
  count: 278
}, {
  category: 'Acquisti',
  count: 234
}];
const topActionsData = [{
  action: 'Utilizzo bici/monopattino',
  count: 234,
  impact: '+15 kg CO₂ risparmiati'
}, {
  action: 'Riciclo corretto rifiuti',
  count: 198,
  impact: '+12 kg rifiuti recuperati'
}, {
  action: 'Spesa a km zero',
  count: 167,
  impact: '+8 kg CO₂ risparmiati'
}, {
  action: 'Riduzione consumo acqua',
  count: 145,
  impact: '+450 L acqua risparmiati'
}, {
  action: 'Uso energia rinnovabile',
  count: 123,
  impact: '+20 kg CO₂ risparmiati'
}];
// Check if data meets privacy threshold
const PRIVACY_THRESHOLD = 30;
const totalUsers = segmentData.reduce((sum, seg) => sum + seg.value, 0);
const meetsThreshold = totalUsers >= PRIVACY_THRESHOLD;
const Insights = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'audience' | 'preferences' | 'actions'>('audience');
  const [activeAIWidget, setActiveAIWidget] = useState<string | null>(null);
  const InsufficientDataCard = () => <div className="rounded-lg border border-gray-200 bg-white p-12" data-id="element-1081">
      <div className="text-center" data-id="element-1082">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-1083" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-1084">
          Dati Insufficienti
        </h2>
        <p className="text-gray-500 max-w-md mx-auto" data-id="element-1085">
          Servono almeno 30 utenti nel periodo selezionato.
        </p>
        <p className="text-sm text-gray-400 mt-2" data-id="element-1086">
          Utenti attuali: {totalUsers}
        </p>
      </div>
    </div>;
  // AI Suggestions for each widget - converted to AIRecommendationPanel format
  const getRecommendationsForWidget = (widgetKey: string) => {
    const widgetData: Record<string, any> = {
      segmentDistribution: {
        title: 'Suggerimenti AI: Distribuzione Segmenti',
        recommendations: [{
          id: '1',
          title: 'Crea premi per Studenti',
          type: 'Opportunità',
          diamondRange: 'Segmento 41%',
          reason: 'Segmento dominante. Offri sconti su trasporti pubblici, abbonamenti bike-sharing, o prodotti eco-friendly per università.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva campagna Senior',
          type: 'Strategia',
          diamondRange: 'Segmento 12%',
          reason: 'Sottorappresentati. Crea premi su risparmio energetico domestico, prodotti per la casa sostenibili, o workshop educativi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      segmentTrend: {
        title: 'Suggerimenti AI: Trend Segmenti',
        recommendations: [{
          id: '1',
          title: 'Capitalizza crescita Famiglie',
          type: 'Trend',
          diamondRange: '+22% ultimo mese',
          reason: 'Accelerazione forte. Crea bundle famiglia: kit prodotti casa sostenibile, esperienze weekend green, sconti spesa bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Prepara offerte estive Studenti',
          type: 'Previsione',
          diamondRange: 'Picco Maggio-Giugno',
          reason: 'Anticipa premi su mobilità estiva, viaggi sostenibili, eventi outdoor eco-friendly.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      preferences: {
        title: 'Suggerimenti AI: Preferenze',
        recommendations: [{
          id: '1',
          title: 'Espandi catalogo Eco-friendly',
          type: 'Opportunità',
          diamondRange: '234 preferenze',
          reason: 'Domanda altissima. Aggiungi: cosmetici bio, abbigliamento sostenibile, accessori riutilizzabili, prodotti zero-waste.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Crea esperienze uniche',
          type: 'Strategia',
          diamondRange: '198 preferenze (+18%)',
          reason: 'Offri: workshop eco-design, visite aziende sostenibili, cooking class bio, tour natura guidati.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      habits: {
        title: 'Suggerimenti AI: Abitudini',
        recommendations: [{
          id: '1',
          title: 'Premia abitudini consolidate',
          type: 'Rinforzo',
          diamondRange: '89-91% sempre',
          reason: 'Risparmio energetico e riciclo già forti. Offri: dispositivi smart home, compostiere domestiche, gadget monitoraggio consumi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Stimola mobilità sostenibile',
          type: 'Incentivo',
          diamondRange: '72% spesso',
          reason: 'Crea: abbonamenti scontati, bonus bici/monopattini, cashback car-sharing elettrico.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      actionsByCategory: {
        title: 'Suggerimenti AI: Azioni per Categoria',
        recommendations: [{
          id: '1',
          title: 'Potenzia categoria Mobilità',
          type: 'Opportunità',
          diamondRange: '456 azioni (+27%)',
          reason: 'Leader assoluto. Aggiungi sfide: "30 giorni in bici", "Zero auto per una settimana", premi per km sostenibili percorsi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva categoria Acquisti',
          type: 'Miglioramento',
          diamondRange: '234 azioni',
          reason: 'Crea: cashback spesa km0, sconti mercati locali, punti extra per prodotti sfusi, partnership negozi bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      topActions: {
        title: 'Suggerimenti AI: Top Azioni',
        recommendations: [{
          id: '1',
          title: 'Gamifica top azioni',
          type: 'Strategia',
          diamondRange: '234 completamenti',
          reason: 'Top 3 molto popolari. Crea: badge progressivi, classifiche mensili, premi milestone (10/50/100 azioni), sfide team.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Diversifica azioni disponibili',
          type: 'Espansione',
          diamondRange: '60% impatto su top 3',
          reason: 'Aggiungi: compostaggio, riparazione oggetti, donazioni, volontariato ambientale.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      }
    };
    return widgetData[widgetKey] || null;
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-1087">
      <div className="w-20 flex-shrink-0" data-id="element-1088">
        <Sidebar data-id="element-1089" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1090">
        <Topbar data-id="element-1091" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-1092">
          <PageHeader title="Insight" subtitle="Preferenze e azioni degli utenti (dati aggregati)." data-id="element-1093" />

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6" data-id="element-1094">
            <nav className="-mb-px flex space-x-8" data-id="element-1095">
              <button onClick={() => setActiveTab('audience')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'audience' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1096">
                <Users className="h-4 w-4 mr-2" data-id="element-1097" />
                Pubblico
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'preferences' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1098">
                <Heart className="h-4 w-4 mr-2" data-id="element-1099" />
                Preferenze e Abitudini
              </button>
              <button onClick={() => setActiveTab('actions')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'actions' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1100">
                <Leaf className="h-4 w-4 mr-2" data-id="element-1101" />
                Azioni Sostenibili
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {!meetsThreshold ? <InsufficientDataCard data-id="element-1102" /> : <div className="space-y-6" data-id="element-1103">
              {/* Audience Tab */}
              {activeTab === 'audience' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1104">
                    {/* Segment Distribution */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1105">
                      <div className="flex items-center justify-between mb-4" data-id="element-1106">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1107">
                          Distribuzione Segmenti
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1108">
                          <button onClick={() => setActiveAIWidget('segmentDistribution')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1109">
                            <span className="text-sm" data-id="element-1110">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1111">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1112">
                          <PieChart data-id="element-1113">
                            <Pie data={segmentData} cx="50%" cy="50%" labelLine={false} label={({
                        name,
                        percent
                      }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-1114">
                              {segmentData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1115" />)}
                            </Pie>
                            <Tooltip data-id="element-1116" />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-1117">
                        {segmentData.map((segment, index) => <div key={index} className="flex items-center" data-id="element-1118">
                            <div className="w-3 h-3 rounded-full mr-2" style={{
                      backgroundColor: segment.color
                    }} data-id="element-1119"></div>
                            <span className="text-sm text-gray-600" data-id="element-1120">
                              {segment.name}: {segment.value}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Segment Trends */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1121">
                      <div className="flex items-center justify-between mb-4" data-id="element-1122">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1123">
                          Trend Segmenti nel Tempo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1124">
                          <button onClick={() => setActiveAIWidget('segmentTrend')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1125">
                            <span className="text-sm" data-id="element-1126">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1127">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1128">
                          <LineChart data={segmentTrendData} data-id="element-1129">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1130" />
                            <XAxis dataKey="month" stroke="#6B7280" fontSize={12} data-id="element-1131" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-1132" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1133" />
                            <Legend data-id="element-1134" />
                            <Line type="monotone" dataKey="studenti" stroke="#1a4d2e" strokeWidth={2} name="Studenti" data-id="element-1135" />
                            <Line type="monotone" dataKey="professionisti" stroke="#2d7a4f" strokeWidth={2} name="Professionisti" data-id="element-1136" />
                            <Line type="monotone" dataKey="famiglie" stroke="#4aa770" strokeWidth={2} name="Famiglie" data-id="element-1137" />
                            <Line type="monotone" dataKey="senior" stroke="#7bc99c" strokeWidth={2} name="Senior" data-id="element-1138" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1139">
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1140">
                      <p className="text-sm text-gray-500" data-id="element-1141">Utenti Totali</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1142">
                        {totalUsers}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1143">
                      <p className="text-sm text-gray-500" data-id="element-1144">
                        Segmento Principale
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1145">
                        Studenti
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1146">
                      <p className="text-sm text-gray-500" data-id="element-1147">Crescita Mensile</p>
                      <p className="text-2xl font-bold text-green-600 mt-1" data-id="element-1148">
                        +8.5%
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1149">
                      <p className="text-sm text-gray-500" data-id="element-1150">Tasso Attività</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1151">
                        73%
                      </p>
                    </div>
                  </div>
                </>}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1152">
                    {/* Main Preferences */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1153">
                      <div className="flex items-center justify-between mb-4" data-id="element-1154">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1155">
                          Preferenze Principali
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1156">
                          <button onClick={() => setActiveAIWidget('preferences')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1157">
                            <span className="text-sm" data-id="element-1158">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1159">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1160">
                          <BarChart data={preferencesData} layout="vertical" data-id="element-1161">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1162" />
                            <XAxis type="number" stroke="#6B7280" fontSize={12} data-id="element-1163" />
                            <YAxis type="category" dataKey="name" stroke="#6B7280" fontSize={12} width={150} data-id="element-1164" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1165" />
                            <Bar dataKey="value" fill="#1a4d2e" radius={[0, 4, 4, 0]} data-id="element-1166" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Habits */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1167">
                      <div className="flex items-center justify-between mb-4" data-id="element-1168">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1169">
                          Abitudini Dichiarate
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1170">
                          <button onClick={() => setActiveAIWidget('habits')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1171">
                            <span className="text-sm" data-id="element-1172">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-1173">
                        {habitsData.map((habit, index) => <div key={index} data-id="element-1174">
                            <div className="flex justify-between items-center mb-2" data-id="element-1175">
                              <span className="text-sm font-medium text-gray-900" data-id="element-1176">
                                {habit.name}
                              </span>
                              <div className="flex items-center" data-id="element-1177">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-1178">
                                  {habit.frequency}
                                </span>
                                <span className="text-sm font-semibold text-greenback" data-id="element-1179">
                                  {habit.value}%
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1180">
                              <div className="bg-greenback h-2 rounded-full" style={{
                        width: `${habit.value}%`
                      }} data-id="element-1181"></div>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1182">
                        <p className="text-xs text-gray-500" data-id="element-1183">
                          Scala frequenza: Sempre (80-100%) • Spesso (60-79%) •
                          A volte (40-59%) • Raramente (&lt;40%)
                        </p>
                      </div>
                    </div>
                  </div>
                </>}

              {/* Actions Tab */}
              {activeTab === 'actions' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1184">
                    {/* Actions by Category */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1185">
                      <div className="flex items-center justify-between mb-4" data-id="element-1186">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1187">
                          Azioni Completate per Categoria
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1188">
                          <button onClick={() => setActiveAIWidget('actionsByCategory')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1189">
                            <span className="text-sm" data-id="element-1190">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1191">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1192">
                          <BarChart data={actionsData} data-id="element-1193">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1194" />
                            <XAxis dataKey="category" stroke="#6B7280" fontSize={12} data-id="element-1195" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-1196" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1197" />
                            <Bar dataKey="count" fill="#1a4d2e" radius={[4, 4, 0, 0]} data-id="element-1198" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Top Actions */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1199">
                      <div className="flex items-center justify-between mb-4" data-id="element-1200">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1201">
                          Top Azioni del Periodo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1202">
                          <button onClick={() => setActiveAIWidget('topActions')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1203">
                            <span className="text-sm" data-id="element-1204">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1205">
                        {topActionsData.map((action, index) => <div key={index} className="flex items-start p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1206">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-greenback text-white flex items-center justify-center font-semibold text-sm mr-3" data-id="element-1207">
                              {index + 1}
                            </div>
                            <div className="flex-1" data-id="element-1208">
                              <div className="flex items-center justify-between" data-id="element-1209">
                                <p className="font-medium text-gray-900 text-sm" data-id="element-1210">
                                  {action.action}
                                </p>
                                <span className="text-sm font-semibold text-greenback" data-id="element-1211">
                                  {action.count}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-1212">
                                <TrendingUp className="h-3 w-3 mr-1" data-id="element-1213" />
                                {action.impact}
                              </p>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1214">
                        <div className="flex items-center justify-between text-sm" data-id="element-1215">
                          <span className="text-gray-500" data-id="element-1216">Totale azioni:</span>
                          <span className="font-semibold text-gray-900" data-id="element-1217">
                            1,669
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Impact Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-6" data-id="element-1218">
                    <div className="flex items-start" data-id="element-1219">
                      <Leaf className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" data-id="element-1220" />
                      <div data-id="element-1221">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1222">
                          Impatto Aggregato del Periodo
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-1223">
                          <div data-id="element-1224">
                            <p className="text-sm text-gray-600" data-id="element-1225">
                              CO₂ Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-1226">
                              +68 kg
                            </p>
                          </div>
                          <div data-id="element-1227">
                            <p className="text-sm text-gray-600" data-id="element-1228">
                              Rifiuti Recuperati
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-1229">
                              +45 kg
                            </p>
                          </div>
                          <div data-id="element-1230">
                            <p className="text-sm text-gray-600" data-id="element-1231">
                              Acqua Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-1232">
                              +1,200 L
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>}
            </div>}
        </main>
      </div>

      {/* AI Sidebar - Using consistent AIRecommendationPanel */}
      {meetsThreshold && activeAIWidget && <AISidebar isOpen={!!activeAIWidget} onClose={() => setActiveAIWidget(null)} data-id="element-1233">
          {(() => {
        const widgetData = getRecommendationsForWidget(activeAIWidget);
        return widgetData ? <AIRecommendationPanel title={widgetData.title} recommendations={widgetData.recommendations} lastUpdated="Ultimi 30 giorni" data-id="element-1234" /> : null;
      })()}
        </AISidebar>}
    </div>;
};
export default Insights;